import { useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { PageContext } from '@/context/PageContext';
import { useResidentSession } from '@/components/Auth/useResidentSession';
import { useToast } from '@/components/Atoms/Toast/useToast';
import { determineAvailableLanguages } from 'utils/determineAvailableLanguages';
import { countryToDataRegion } from 'utils/dataRegion';
import { correctLocaleCasing } from 'utils/correctLocaleCasing';
import { getLanguageCodeFromKey, LanguageCode } from '@/i18n/languageCodesToKey';
import { useResidentLanguagePreference } from './useResidentLanguagePreference';

const SESSION_KEY = 'lang-pref-toast-dismissed';

function dismissedForSession(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return true;
  }
}

function markDismissed() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // sessionStorage unavailable (private mode, etc.) — fail silently
  }
}

function buildLocaleSwappedHref(slug: string[] | undefined, asPath: string, targetLocale: string): string {
  if (!slug || slug.length < 2) return asPath;
  const newSlug = [...slug];
  newSlug[1] = targetLocale;
  const queryString = asPath.split('?')[1];
  const base = `/${newSlug.join('/')}`;
  return queryString ? `${base}?${queryString}` : base;
}

export function useLanguagePreferenceToast() {
  const router = useRouter();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const pageContext = useContext(PageContext);
  const { urlParams, slug, theme, organization, page, questionnaire, survey } = pageContext;
  const { isAuthenticated, isSessionLoading } = useResidentSession();
  const isEligibleUser = isAuthenticated && !isSessionLoading;
  const region = countryToDataRegion(organization?.country);
  const { preferredLanguage } = useResidentLanguagePreference({ region });
  const currentLocale = urlParams?.locale;
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (hasShownRef.current) return;
    if (!isEligibleUser) return;
    if (!preferredLanguage || !currentLocale) return;
    if (correctLocaleCasing(preferredLanguage) === correctLocaleCasing(currentLocale)) return;
    if (dismissedForSession()) return;

    const allowedLanguages = determineAvailableLanguages({ organization, theme, page, questionnaire, survey });
    if (!allowedLanguages.includes(preferredLanguage)) return;

    hasShownRef.current = true;

    const preferredLanguageName =
      getLanguageCodeFromKey(correctLocaleCasing(preferredLanguage) as LanguageCode)?.localTitle ?? preferredLanguage;

    showToast({
      message: t('languagePreference.toast.message', {
        defaultValue: 'View this page in {{language}}?',
        language: preferredLanguageName,
      }),
      variant: 'success',
      actions: [
        {
          label: t('languagePreference.toast.ignore', { defaultValue: 'Ignore' }),
          onClick: () => {
            markDismissed();
          },
        },
        {
          label: t('languagePreference.toast.switch', { defaultValue: 'Switch language' }),
          onClick: () => {
            markDismissed();
            const href = buildLocaleSwappedHref(slug, router.asPath, preferredLanguage);
            router.push(href).catch(() => {
              // ignore navigation errors
            });
          },
        },
      ],
    });
  }, [
    isEligibleUser,
    preferredLanguage,
    currentLocale,
    organization,
    theme,
    page,
    questionnaire,
    survey,
    slug,
    router,
    showToast,
    t,
  ]);
}
